import React, { useRef, useState, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MilestoneHeaderCell from './MilestoneHeaderCell';
import ItemCount from './ItemCount';
import TouchPadIcon from '../components/TouchPadIcon';
import SortIcon from 'app/assets/svgIcons/SortIcon';
import { ColumnSortMenu, SortMenuOption } from '../components/ColumnSortMenu';
import { useMilestonesGridSlice } from '../slice';
import { selectSortSelections } from '../slice/selectors';
import styles from '../SplitScrollTable.module.scss';

interface ColumnConfig {
  key: string;
  label: string;
  field?: string;
  milestoneName?: string;
  width?: string;
  approvalRequired?: boolean;
  async?: boolean;
  showTouchPadIcon?: boolean;
  showSortIcon?: boolean;
  sortOptions?: SortMenuOption[];
}

interface TableHeadProps {
  columns: ColumnConfig[];
  itemCount?: number; // Optional item count for meta table
  onTouchPadClick?: (columnKey: string) => void; // Optional touchpad click handler
  /** When false, meta column toggle chevron is rotated 180° */
  metaTableCollapsed?: boolean;
}

const TableHead: React.FC<TableHeadProps> = memo(
  ({ columns, itemCount, onTouchPadClick, metaTableCollapsed = true }) => {
    const dispatch = useDispatch();
    const { actions } = useMilestonesGridSlice();

    // Get sort selections from Redux
    const sortSelections = useSelector(selectSortSelections);

    // Local state for menu open/close
    const [openColumnKey, setOpenColumnKey] = useState<string | null>(null);

    // Store refs for sort icon elements (for menu positioning)
    const sortIconRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const toggleMenu = useCallback((columnKey: string) => {
      setOpenColumnKey(prev => (prev === columnKey ? null : columnKey));
    }, []);

    const closeMenu = useCallback(() => {
      setOpenColumnKey(null);
    }, []);

    const handleOptionToggle = useCallback(
      (columnKey: string, optionKey: string) => {
        dispatch(actions.setSortSelection({ columnKey, optionKey }));
      },
      [dispatch, actions]
    );

    const getColumnSelections = useCallback(
      (columnKey: string): Set<string> => {
        // Name column: independent selections
        if (columnKey === 'name') {
          return new Set(sortSelections.name || []);
        }

        // Shared columns (date, tier, etc): check if this column has active shared selections
        const selections: string[] = [];

        // Check if this column has the active shared sort
        if (
          sortSelections.sharedSortColumn === columnKey &&
          sortSelections.sharedSort
        ) {
          selections.push(sortSelections.sharedSort);
        }

        // Check if this column has the active shared pin
        if (
          sortSelections.sharedPinColumn === columnKey &&
          sortSelections.sharedPin
        ) {
          selections.push(sortSelections.sharedPin);
        }

        return new Set(selections);
      },
      [sortSelections]
    );

    const hasSortApplied = useCallback(
      (columnKey: string): boolean => {
        // Name column: check if name sort is active (exclude pins)
        if (columnKey === 'name') {
          const nameSelections = sortSelections.name || [];
          // Filter out any pin selections, only count actual sorts
          return nameSelections.some(s => s !== 'pinLeft' && s !== 'pinRight');
        }

        // Shared columns: check if this column has the active shared sort
        return (
          sortSelections.sharedSortColumn === columnKey &&
          !!sortSelections.sharedSort
        );
      },
      [sortSelections]
    );

    return (
      <thead>
        <tr>
          {columns.map(column => {
            // Check if this is a milestone column (has milestoneName)
            const isMilestoneColumn = !!column.milestoneName;
            // Check if this is a phase date column
            const isPhaseDate = !!(column as any).phaseDate;
            // Check if this is the moment name column
            const isMomentNameColumn = column.key === 'name';
            const displayLabel = isPhaseDate
              ? column.label.replace(' ', '\n')
              : column.label;

            const isMenuOpen = openColumnKey === column.key;
            const columnSelections = getColumnSelections(column.key);
            const isSortActive = hasSortApplied(column.key);

            return (
              <th
                key={column.key}
                data-column-key={column.key}
                data-phase-date={isPhaseDate ? 'true' : undefined}
                data-menu-open={isMenuOpen ? 'true' : undefined}
                data-sort-active={isSortActive ? 'true' : undefined}
                style={
                  isPhaseDate
                    ? { width: '100px' }
                    : column.width
                    ? { width: column.width }
                    : undefined
                }
              >
                {isMilestoneColumn || isPhaseDate ? (
                  <MilestoneHeaderCell
                    label={displayLabel}
                    approvalRequired={column.approvalRequired}
                    async={column.async}
                    className={
                      isPhaseDate ? styles.betaHeaderContent : undefined
                    }
                    labelClassName={
                      isPhaseDate ? styles.betaHeaderLabel : undefined
                    }
                    labelBoxClassName={
                      isPhaseDate ? styles.betaHeaderLabelBox : undefined
                    }
                  />
                ) : (
                  // Meta columns - simple text with optional Sort and TouchPad icons
                  <div className={styles.headerContent}>
                    <span>{column.label}</span>
                    {isMomentNameColumn && itemCount !== undefined && (
                      <ItemCount count={itemCount} />
                    )}
                    {column.showSortIcon && (
                      <>
                        <div
                          ref={el => {
                            sortIconRefs.current[column.key] = el;
                          }}
                          style={{ display: 'inline-block' }}
                        >
                          <SortIcon
                            className="sort-icon"
                            onClick={() => toggleMenu(column.key)}
                          />
                        </div>
                        {column.sortOptions &&
                          column.sortOptions.length > 0 && (
                            <ColumnSortMenu
                              columnKey={column.key}
                              options={column.sortOptions}
                              selectedOptions={getColumnSelections(column.key)}
                              onOptionToggle={handleOptionToggle}
                              isOpen={openColumnKey === column.key}
                              onClose={closeMenu}
                              anchorElement={sortIconRefs.current[column.key]}
                            />
                          )}
                      </>
                    )}
                    {column.showTouchPadIcon && (
                      <TouchPadIcon
                        className="touchpad-icon"
                        collapsed={metaTableCollapsed}
                        onClick={() => onTouchPadClick?.(column.key)}
                      />
                    )}
                  </div>
                )}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  }
);

TableHead.displayName = 'TableHead';

export default TableHead;
